/**
 * Пагинация (PaginationControlled) — управляемая через props.
 * Перенесено из web-client UI/Pagination, адаптировано под skeleton.
 */

import type { Dispatch, FC, SetStateAction } from 'react';
import { useState } from 'react';
import styled, { css } from 'styled-components';
import type { SpaceProps } from 'styled-system';
import { space as styledSpace } from 'styled-system';

import { Dropdown } from '../Dropdown';
import { IconChevronDown, IconChevronLeft, IconChevronRight } from '../icons';
import { getTotalPages, getVisiblePages } from './utils';
import { DEFAULT_ITEMS_PER_PAGE_OPTIONS } from './utils';

const PageButton = styled.button<{ $isActive?: boolean }>(
  ({ theme: { space, colors, borderRadius }, $isActive }) => css`
    display: inline-flex;
    align-items: center;
    justify-content: center;
    min-width: ${space[3]}px;
    height: ${space[3]}px;
    padding: 0;
    margin: 0;
    border: none;
    background: transparent;
    color: ${colors.text.secondary};
    border-radius: ${borderRadius}px;
    line-height: ${space[3]}px;
    font-weight: inherit;
    font-size: inherit;
    font-family: inherit;
    text-align: center;
    cursor: pointer;
    user-select: none;
    transition: background 150ms, color 150ms;

    ${$isActive &&
    css`
      background-color: ${colors.highlight.gray25};
      color: ${colors.text.primary};
    `}

    &:hover:not(:disabled) {
      background-color: ${colors.highlight.gray25};
      color: ${colors.text.primary};
    }

    &:disabled {
      cursor: default;
      opacity: 0.5;
    }
  `,
);

const ItemsPerPageTrigger = styled.button<{ $open?: boolean }>(
  ({ theme: { space, colors, borderRadius }, $open }) => css`
    display: flex;
    align-items: center;
    padding: 0 10px;
    height: 28px;
    border: none;
    background: transparent;
    color: ${colors.text.secondary};
    border-radius: ${borderRadius}px;
    font-size: inherit;
    font-family: inherit;
    cursor: pointer;
    user-select: none;
    transition: background 150ms, color 150ms;

    & svg {
      margin-left: ${space[1]}px;
    }

    ${$open &&
    css`
      background: ${colors.highlight.gray25};
      color: ${colors.text.primary};
    `}

    &:hover {
      background-color: ${colors.highlight.gray25};
      color: ${colors.text.primary};
    }
  `,
);

const PerPageOption = styled.button(
  ({ theme: { space, colors } }) => css`
    display: block;
    width: 100%;
    min-width: 48px;
    height: ${space[4]}px;
    padding: 0 12px;
    border: none;
    background: transparent;
    color: ${colors.text.secondary};
    border-radius: 0;
    font-size: inherit;
    font-family: inherit;
    text-align: left;
    cursor: pointer;
    transition: background 150ms, color 150ms;

    &:hover {
      background-color: ${colors.highlight.gray25};
      color: ${colors.text.primary};
    }
  `,
);

const PagesWrapper = styled.ul`
  display: flex;
  align-items: center;
  column-gap: 2px;
  margin: 0;
  padding: 0;
  list-style: none;
`;

const PaginationContainer = styled.div<SpaceProps>(
  ({ theme: { colors } }) => css`
    display: flex;
    align-items: center;
    justify-content: flex-start;
    gap: 16px;
    border-top: 1px solid ${colors.divider};
    min-height: 53px;
    padding-top: 12px;

    ${styledSpace}
  `,
);

const DotsStyle = { width: 28, justifyContent: 'center' as const };

export interface PaginationControlledProps extends SpaceProps {
  page: number;
  itemsPerPage: number;
  totalItems: number;
  setPage: Dispatch<SetStateAction<number>>;
  setItemsPerPage: Dispatch<SetStateAction<number>>;
  itemsPerPageOptions?: number[];
}

export const PaginationControlled: FC<PaginationControlledProps> = ({
  page,
  itemsPerPage,
  totalItems,
  setPage,
  setItemsPerPage,
  itemsPerPageOptions = DEFAULT_ITEMS_PER_PAGE_OPTIONS,
  ...other
}) => {
  const [dropdownOpen, setDropdownOpen] = useState(false);

  const totalPages = getTotalPages(totalItems, itemsPerPage);
  const visiblePages = getVisiblePages(page, totalPages);
  const showPagination = totalItems > itemsPerPageOptions[0];

  const handleItemsPerPageChange = (value: number) => {
    setItemsPerPage(value);
    setPage(1);
    setDropdownOpen(false);
  };

  if (!showPagination) return null;

  return (
    <PaginationContainer {...other}>
      <Dropdown
        open={dropdownOpen}
        onOpenChange={setDropdownOpen}
        placement="top-start"
        width="48px"
        trigger={
          <ItemsPerPageTrigger
            type="button"
            aria-label="Показать по"
            aria-expanded={dropdownOpen}
            $open={dropdownOpen}
          >
            Показать по:&nbsp;{itemsPerPage}
            <IconChevronDown />
          </ItemsPerPageTrigger>
        }
      >
        {itemsPerPageOptions.map((option) => (
          <PerPageOption
            key={option}
            type="button"
            aria-label={`Показать по ${option}`}
            onClick={() => handleItemsPerPageChange(option)}
          >
            {option}
          </PerPageOption>
        ))}
      </Dropdown>
      {totalPages > 1 && (
        <nav aria-label="Выбор страницы">
          <PagesWrapper>
            {page !== 1 && (
              <li>
                <PageButton
                  type="button"
                  aria-label="Предыдущая страница"
                  onClick={() => setPage((prev) => prev - 1)}
                >
                  <IconChevronLeft />
                </PageButton>
              </li>
            )}
            {visiblePages.map((visiblePage, index) => {
              const isPage = typeof visiblePage === 'number';
              const isActive = visiblePage === page;

              if (!isPage) {
                return (
                  <li key={`dots-${index}`} style={DotsStyle}>
                    {visiblePage}
                  </li>
                );
              }

              return (
                <li key={visiblePage}>
                  <PageButton
                    type="button"
                    aria-label={`Страница ${visiblePage}`}
                    aria-current={isActive ? 'page' : undefined}
                    $isActive={isActive}
                    onClick={() => setPage(visiblePage)}
                  >
                    {visiblePage}
                  </PageButton>
                </li>
              );
            })}
            {page < totalPages && (
              <li>
                <PageButton
                  type="button"
                  aria-label="Следующая страница"
                  onClick={() => setPage((prev) => prev + 1)}
                >
                  <IconChevronRight />
                </PageButton>
              </li>
            )}
          </PagesWrapper>
        </nav>
      )}
    </PaginationContainer>
  );
};
